import React, { useMemo } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    useTheme,
    Card,
    Tooltip as MuiTooltip,
    LinearProgress,
    Chip
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    AreaChart,
    Area,
    ComposedChart
} from 'recharts';
import { motion } from 'framer-motion';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import PaymentIcon from '@mui/icons-material/Payment';
import CategoryIcon from '@mui/icons-material/Category';
import ScaleIcon from '@mui/icons-material/Scale';
import StarIcon from '@mui/icons-material/Star';

// Grade config: code -> label + color
const GRADE_CONFIG = {
    GOLD: { label: 'Gold', color: '#f59e0b' },
    SILVR: { label: 'Silver', color: '#94a3b8' },
    DMNND: { label: 'Diamond', color: '#3b82f6' },
    PLTNUM: { label: 'Platinum', color: '#8b5cf6' },
};

// --- Utilities ---
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(value);
};

const parseTime = (timeStr) => {
    if (!timeStr) return null;
    try {
        const [time, period] = timeStr.trim().split(" ");
        const [hours, minutes] = time.split(":");
        let hour24 = parseInt(hours);
        if (period === "PM" && hour24 !== 12) hour24 += 12;
        if (period === "AM" && hour24 === 12) hour24 = 0;
        return { hours: hour24, minutes: parseInt(minutes) || 0 };
    } catch (e) {
        return null;
    }
};

// --- Motion Components ---
const MotionPaper = motion(Paper);

// --- Constants ---
const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const STATUS_COLORS = {
    'Delivered': '#10b981',
    'Cancelled': '#ef4444',
    'Pending': '#f59e0b',
    'Processing': '#3b82f6',
    'Shipped': '#8b5cf6'
};
const GLASS_BG = 'rgba(255, 255, 255, 0.7)';
const GLASS_BORDER = '1px solid rgba(255, 255, 255, 0.3)';
const GLASS_SHADOW = '0 8px 32px 0 rgba(31, 38, 135, 0.07)';

const SalesDashboard = ({ data }) => {
    const theme = useTheme();

    // --- Data Processing ---
    const kpiData = useMemo(() => {
        if (!data || data.length === 0) return {
            totalRevenue: 0,
            totalOrders: 0,
            avgOrderValue: 0,
            deliveredCount: 0,
            avgDeliveryTimeMinutes: 0,
            onTimePercentage: 0,
            totalVolume: 0,
            totalQty: 0
        };

        let totalRevenue = 0;
        let totalDeliveryTimeMinutes = 0;
        let deliveredWithTimeCount = 0;
        let onTimeCount = 0;
        let deliveredCount = 0;

        let totalVolume = 0;
        let totalQty = 0;

        data.forEach(order => {
            // Revenue
            let orderTotal = 0;
            try {
                const parsed = order.saleitemsum ? JSON.parse(order.saleitemsum) : {};
                orderTotal = (parsed.TotalAmount || 0) + (order.DeliveryCharge || 0);
            } catch (e) {
                orderTotal = order.TotalAmount || 0;
            }
            totalRevenue += orderTotal;

            // Status Counts
            const status = order.SystemStatus || 'Unknown';
            if (status === 'Delivered') deliveredCount++;

            // Volume Calculation (Replicating Excel Logic)
            if (order.itemline && Array.isArray(order.itemline)) {
                order.itemline.forEach(itm => {
                    const qty = itm.Qty || 0;
                    totalQty += qty;

                    let itemVolume = 0;
                    if (itm.Units != null && itm.Units !== undefined) {
                        itemVolume = itm.Units * qty;
                    } else if (Array.isArray(itm.BOM) && itm.BOM.length > 0 && itm.BOM[0].UnitWt != null) {
                        itemVolume = qty * itm.BOM[0].UnitWt;
                    }
                    totalVolume += itemVolume;
                });
            }

            // Delivery Time & On-Time Logic
            if (order.DeliveryDateTime && order.PickUpDateTime && status === 'Delivered') {
                const start = new Date(order.PickUpDateTime);
                const end = new Date(order.DeliveryDateTime);
                if (!isNaN(start) && !isNaN(end) && end > start) {
                    const diffMinutes = (end - start) / (1000 * 60);
                    totalDeliveryTimeMinutes += diffMinutes;
                    deliveredWithTimeCount++;
                }
            }

            // On-Time Calculation
            if (order.DeliveryDateTime && order.DeliveryScheduleDate && order.TimeSlotName) {
                const deliveryDate = new Date(order.DeliveryDateTime);
                const scheduleDate = new Date(order.DeliveryScheduleDate);
                const timeSlot = order.TimeSlotName.split(' - ');

                // Check Date Diff
                const dateDiff = Math.floor((deliveryDate - scheduleDate) / (1000 * 60 * 60 * 24));

                if (dateDiff < 0) {
                    onTimeCount++; // Early is on time
                } else if (dateDiff === 0) {
                    // Same Day, check time
                    if (timeSlot.length === 2) {
                        const endTime = parseTime(timeSlot[1]);
                        if (endTime) {
                            const slotEnd = new Date(scheduleDate);
                            slotEnd.setHours(endTime.hours, endTime.minutes, 0, 0);
                            if (deliveryDate <= slotEnd) {
                                onTimeCount++;
                            }
                        }
                    }
                }
            }
        });

        return {
            totalRevenue,
            totalOrders: data.length,
            avgOrderValue: data.length > 0 ? totalRevenue / data.length : 0,
            deliveredCount,
            avgDeliveryTimeMinutes: deliveredWithTimeCount > 0 ? Math.round(totalDeliveryTimeMinutes / deliveredWithTimeCount) : 0,
            onTimePercentage: data.length > 0 ? Math.round((onTimeCount / data.length) * 100) : 0,
            totalVolume: totalVolume.toFixed(2),
            totalQty: totalQty.toFixed(2)
        };
    }, [data]);

    const hourlySalesData = useMemo(() => {
        const hoursMap = new Array(24).fill(0).map((_, i) => ({ hour: i, count: 0, amount: 0 }));
        data.forEach(order => {
            const dateStr = order.DeliveryDateTime || order.DeliveryScheduleDate || order.OrderDate;
            if (dateStr) {
                const date = new Date(dateStr);
                const hour = date.getHours();
                if (hour >= 0 && hour < 24) {
                    hoursMap[hour].count += 1;
                    let amount = 0;
                    try {
                        const parsed = order.saleitemsum ? JSON.parse(order.saleitemsum) : {};
                        amount = (parsed.TotalAmount || 0) + (order.DeliveryCharge || 0);
                    } catch (e) {
                        amount = order.TotalAmount || 0;
                    }
                    hoursMap[hour].amount += amount;
                }
            }
        });
        return hoursMap.map(h => ({ name: `${h.hour}:00`, orders: h.count, revenue: h.amount }));
    }, [data]);

    const topAreasData = useMemo(() => {
        const areaMap = {};
        data.forEach(order => {
            const area = order.AreaName || 'Unknown';
            if (!areaMap[area]) areaMap[area] = 0;
            areaMap[area] += 1;
        });
        return Object.entries(areaMap)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);
    }, [data]);

    const topItemsData = useMemo(() => {
        const itemMap = {};
        data.forEach(order => {
            if (order.itemline && Array.isArray(order.itemline)) {
                order.itemline.forEach(item => {
                    const itemName = item.ItemName || 'Unknown Item';
                    if (!itemMap[itemName]) itemMap[itemName] = 0;
                    itemMap[itemName] += (item.Qty || 0);
                });
            }
        });
        return Object.entries(itemMap)
            .map(([name, qty]) => ({ name, qty }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 5);
    }, [data]);

    const paymentModeData = useMemo(() => {
        const paymentMap = {};
        data.forEach(order => {
            if (order.payment && Array.isArray(order.payment)) {
                order.payment.forEach(p => {
                    const mode = p.PaymentGatewayName || 'Unknown';
                    paymentMap[mode] = (paymentMap[mode] || 0) + 1;
                });
            } else {
                paymentMap['Unknown'] = (paymentMap['Unknown'] || 0) + 1;
            }
        });
        return Object.keys(paymentMap).map(key => ({ name: key, value: paymentMap[key] }));
    }, [data]);

    const deliveryPerformanceData = useMemo(() => {
        let onTime = 0; let delayed = 0; let early = 0;
        data.forEach(order => {
            if (!order.DeliveryDateTime || !order.DeliveryScheduleDate || !order.TimeSlotName) return;
            const deliveryDate = new Date(order.DeliveryDateTime);
            const scheduleDate = new Date(order.DeliveryScheduleDate);
            const timeSlot = order.TimeSlotName.split(' - ');
            const dateDiff = Math.floor((deliveryDate - scheduleDate) / (1000 * 60 * 60 * 24));

            if (dateDiff > 0) delayed++;
            else if (dateDiff < 0) early++;
            else {
                if (timeSlot.length === 2) {
                    const endTime = parseTime(timeSlot[1]);
                    if (endTime) {
                        const slotEnd = new Date(scheduleDate);
                        slotEnd.setHours(endTime.hours, endTime.minutes, 0, 0);
                        if (deliveryDate > slotEnd) delayed++;
                        else onTime++;
                    }
                }
            }
        });
        return [
            { name: 'On Time', value: onTime, color: '#10b981' },
            { name: 'Delayed', value: delayed, color: '#ef4444' },
            { name: 'Early', value: early, color: '#3b82f6' }
        ].filter(d => d.value > 0);
    }, [data]);

    const salesTrendData = useMemo(() => {
        const trendMap = {};
        data.forEach(order => {
            const dateStr = order.DeliveryDateTime ? order.DeliveryDateTime.split('T')[0] : (order.DeliveryScheduleDate ? order.DeliveryScheduleDate.split('T')[0] : 'Unknown');
            let amount = 0;
            try {
                const parsed = order.saleitemsum ? JSON.parse(order.saleitemsum) : {};
                amount = (parsed.TotalAmount || 0) + (order.DeliveryCharge || 0);
            } catch (e) {
                amount = order.TotalAmount || 0;
            }
            if (!trendMap[dateStr]) trendMap[dateStr] = { date: dateStr, amount: 0, count: 0 };
            trendMap[dateStr].amount += amount;
            trendMap[dateStr].count += 1;
        });
        return Object.values(trendMap).sort((a, b) => new Date(a.date) - new Date(b.date));
    }, [data]);

    const dailyVolumeTrend = useMemo(() => {
        const trendMap = {};
        data.forEach(order => {
            const dateStr = order.DeliveryDateTime ? order.DeliveryDateTime.split('T')[0] : (order.DeliveryScheduleDate ? order.DeliveryScheduleDate.split('T')[0] : 'Unknown');
            let orderVolume = 0;
            if (order.itemline && Array.isArray(order.itemline)) {
                order.itemline.forEach(itm => {
                    const qty = itm.Qty || 0;
                    let itemVolume = 0;
                    if (itm.Units != null && itm.Units !== undefined) {
                        itemVolume = itm.Units * qty;
                    } else if (Array.isArray(itm.BOM) && itm.BOM.length > 0 && itm.BOM[0].UnitWt != null) {
                        itemVolume = qty * itm.BOM[0].UnitWt;
                    }
                    orderVolume += itemVolume;
                });
            }

            if (!trendMap[dateStr]) trendMap[dateStr] = { date: dateStr, volume: 0, count: 0 };
            trendMap[dateStr].volume += orderVolume;
            trendMap[dateStr].count += 1;
        });
        return Object.values(trendMap)
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map(d => ({ ...d, volume: parseFloat(d.volume.toFixed(2)) }));
    }, [data]);

    const dayOfWeekData = useMemo(() => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayMap = {};
        days.forEach(day => dayMap[day] = 0);

        data.forEach(order => {
            if (order.DeliveryDateTime || order.DeliveryScheduleDate) {
                const date = new Date(order.DeliveryDateTime || order.DeliveryScheduleDate);
                if (!isNaN(date)) {
                    const dayName = days[date.getDay()];
                    let amount = 0;
                    try {
                        const parsed = order.saleitemsum ? JSON.parse(order.saleitemsum) : {};
                        amount = (parsed.TotalAmount || 0) + (order.DeliveryCharge || 0);
                    } catch (e) {
                        amount = order.TotalAmount || 0;
                    }
                    dayMap[dayName] += amount;
                }
            }
        });

        // Reorder to start from Monday as per business preference usually, but standard is fine. 
        // Let's stick to standard week order or sort by value? User said "Which day it sale more", so maybe sort by value or keep day order.
        // Keeping day order (Mon-Sun) is best for "Trend" understanding, but for "Which day more" sorting might be better.
        // I will keep Mon-Sun order for consistency with a "Week" view.

        const orderedDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        return orderedDays.map(day => ({ name: day, value: dayMap[day] }));
    }, [data]);

    const gradeData = useMemo(() => {
        const gradeMap = {};
        data.forEach(order => {
            const raw = order.Grade || 'OTHER';
            const key = GRADE_CONFIG[raw] ? raw : 'OTHER';
            gradeMap[key] = (gradeMap[key] || 0) + 1;
        });
        return Object.entries(gradeMap).map(([key, count]) => ({
            key,
            name: GRADE_CONFIG[key]?.label || 'Other',
            value: count,
            color: GRADE_CONFIG[key]?.color || '#6b7280'
        })).sort((a, b) => b.value - a.value);
    }, [data]);


    // --- Components ---
    const GlassCard = ({ children, delay = 0, sx = {} }) => (
        <MotionPaper
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
            sx={{
                p: 2.5,
                borderRadius: 4,
                bgcolor: GLASS_BG,
                backdropFilter: 'blur(10px)',
                border: GLASS_BORDER,
                boxShadow: GLASS_SHADOW,
                height: '100%',
                ...sx
            }}
        >
            {children}
        </MotionPaper>
    );

    const KpiCard = ({ title, value, subtext, icon, color, delay }) => (
        <GlassCard delay={delay} sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: -10, right: -10, width: 100, height: 100, borderRadius: '50%', background: color, opacity: 0.1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: `${color}15`, color: color }}>
                    {icon}
                </Box>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1e293b' }}>{value}</Typography>
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>{title}</Typography>
            {subtext && <Typography variant="caption" sx={{ color: color, fontWeight: 600, mt: 1, display: 'block' }}>{subtext}</Typography>}
        </GlassCard>
    );

    return (
        <Box sx={{ p: 1, backgroundColor: '#f8fafc', minHeight: '80vh' }}>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard title="Total Revenue" value={formatCurrency(kpiData.totalRevenue)} subtext={`${kpiData.totalOrders} total orders`} icon={<AttachMoneyIcon />} color="#6366f1" delay={0.1} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard title="Avg. Order Value" value={formatCurrency(kpiData.avgOrderValue)} subtext="Per successful order" icon={<TrendingUpIcon />} color="#10b981" delay={0.2} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard title="Avg. Delivery Time" value={`${kpiData.avgDeliveryTimeMinutes} min`} subtext="Pickup to Delivery" icon={<TimerIcon />} color="#f59e0b" delay={0.3} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard title="On-Time Rate" value={`${kpiData.onTimePercentage}%`} subtext={`${kpiData.deliveredCount} delivered`} icon={<LocalShippingIcon />} color="#ec4899" delay={0.4} />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <KpiCard title="Total Volume & Qty" value={`${kpiData.totalVolume} Kg`} subtext={`Qty: ${kpiData.totalQty} Units`} icon={<ScaleIcon />} color="#3b82f6" delay={0.5} />
                </Grid>
            </Grid>

            {/* Row 2: Hourly & Top Areas */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <GlassCard delay={0.6} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#334155' }}>Hourly Delivery Activity (Time)</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <AreaChart data={hourlySalesData}>
                                <defs>
                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }} formatter={(val, name) => [name === 'revenue' ? formatCurrency(val) : val, name === 'revenue' ? 'Revenue' : 'Orders']} />
                                <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <GlassCard delay={0.7} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#334155' }}>Top Delivery Areas</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart layout="vertical" data={topAreasData} margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#475569', fontSize: 11, fontWeight: 500 }} />
                                <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: 8 }} />
                                <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={24} />
                            </BarChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
            </Grid>

            {/* Row 3: Daily Volume Trend & Delivery Performance (Replaces Order Status Pie) */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <GlassCard delay={0.8} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#334155' }}>Daily Volume Trend (Kg)</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <AreaChart data={dailyVolumeTrend}>
                                <defs>
                                    <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="date" axisLine={false} tickLine={false} tickFormatter={(str) => { const date = new Date(str); return `${date.getDate()}/${date.getMonth() + 1}`; }} />
                                <YAxis axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(val) => [typeof val === 'number' ? `${val.toFixed(2)} Kg` : val, 'Volume']} />
                                <Area type="monotone" dataKey="volume" stroke="#8b5cf6" strokeWidth={3} fill="url(#colorVolume)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <GlassCard delay={0.9} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#334155' }}>Delivery Performance</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <PieChart>
                                <Pie data={deliveryPerformanceData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                    {deliveryPerformanceData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
            </Grid>

            {/* Row 4: Top Items, Payment Modes & Customer Grade */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <GlassCard delay={1.0} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#334155' }}>Top Selling Items (Qty)</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart layout="vertical" data={topItemsData} margin={{ left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" />
                                <XAxis type="number" hide />
                                <YAxis dataKey="name" type="category" width={120} tick={{ fill: '#475569', fontSize: 11 }} />
                                <Tooltip cursor={{ fill: 'transparent' }} />
                                <Bar dataKey="qty" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <GlassCard delay={1.1} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#334155' }}>Payment Modes</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart data={paymentModeData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} />
                                <Tooltip cursor={{ fill: '#f1f5f9' }} />
                                <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
                {/* Grade Distribution Card */}
                <Grid item xs={12} md={4}>
                    <GlassCard delay={1.2} sx={{ height: 400 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{ p: 0.8, borderRadius: 2, bgcolor: '#fef3c7', color: '#d97706', display: 'flex' }}>
                                <StarIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>Customer Grade Mix</Typography>
                        </Box>
                        {gradeData.length === 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                                <Typography variant="body2" color="text.secondary">No grade data available</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '88%', gap: 1 }}>
                                <ResponsiveContainer width="100%" height="60%">
                                    <PieChart>
                                        <Pie
                                            data={gradeData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={48}
                                            outerRadius={72}
                                            paddingAngle={4}
                                            dataKey="value"
                                        >
                                            {gradeData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(val, name) => [val + ' orders', name]}
                                            contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                                {/* Grade legend table */}
                                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                                    {gradeData.map((g) => (
                                        <Box
                                            key={g.key}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 0.6,
                                                px: 1,
                                                borderRadius: 2,
                                                mb: 0.5,
                                                bgcolor: `${g.color}12`,
                                                border: `1px solid ${g.color}30`
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color, flexShrink: 0 }} />
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', fontSize: '11px' }}>
                                                    {g.name}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: g.color, fontSize: '12px' }}>
                                                    {g.value}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '10px' }}>
                                                    ({data.length > 0 ? Math.round((g.value / data.length) * 100) : 0}%)
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </GlassCard>
                </Grid>
            </Grid>

            {/* Row 5: Sales Trend & Day of Week */}
            <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                    <GlassCard delay={1.2} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#334155' }}>Sales Revenue Trend</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <AreaChart data={salesTrendData}>
                                <defs>
                                    <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="date" axisLine={false} tickLine={false} tickFormatter={(str) => { const date = new Date(str); return `${date.getDate()}/${date.getMonth() + 1}`; }} />
                                <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `₹${val / 1000}k`} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(val) => formatCurrency(val)} />
                                <Area type="monotone" dataKey="amount" stroke="#10b981" strokeWidth={3} fill="url(#colorAmount)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
                <Grid item xs={12} md={6}>
                    <GlassCard delay={1.3} sx={{ height: 400 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#334155' }}>Day of Week Sales (Revenue)</Typography>
                        <ResponsiveContainer width="100%" height="85%">
                            <BarChart data={dayOfWeekData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                                <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => `₹${val / 1000}k`} />
                                <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: 12, border: 'none' }} formatter={(val) => formatCurrency(val)} />
                                <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </GlassCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SalesDashboard;
